import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

const StarRow = ({ rating, size = 'sm' }) => {
  const sizeClass = size === 'lg' ? 'w-5 h-5' : 'w-3.5 h-3.5';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5]?.map((star) => (
        <svg
          key={star}
          viewBox="0 0 12 12"
          fill={star <= Math.round(rating) ? '#ffd700' : 'none'}
          stroke={star <= Math.round(rating) ? '#ffd700' : '#d1d5db'}
          strokeWidth="1"
          className={sizeClass}
        >
          <path d="M6 1l1.4 2.8L10.5 4.3l-2.25 2.2.53 3.1L6 8.1 3.22 9.6l.53-3.1L1.5 4.3l3.1-.5z" />
        </svg>
      ))}
    </div>
  );
};

const DriverProfileModal = ({ vehicleType, vehicleLabel, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [driverData, setDriverData] = useState(null);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    if (!vehicleType) return;
    fetchDriverProfile();
  }, [vehicleType]);

  const fetchDriverProfile = async () => {
    setLoading(true);
    try {
      // Fetch a representative driver for this vehicle type with their profile
      const { data: driverProfiles, error: driverError } = await supabase?.from('driver_profiles')?.select(`
          id,
          vehicle_type,
          vehicle_name,
          vehicle_color,
          vehicle_year,
          license_plate,
          rating,
          total_trips,
          is_verified,
          status,
          user_profiles!driver_profiles_user_id_fkey (
            full_name,
            avatar_url
          )
        `)?.eq('vehicle_type', vehicleType)?.eq('is_verified', true)?.order('total_trips', { ascending: false })?.limit(1);

      if (driverError) throw driverError;

      let driver = driverProfiles?.[0] || null;

      // If no verified driver, try any driver of this type
      if (!driver) {
        const { data: anyDriver } = await supabase?.from('driver_profiles')?.select(`
            id,
            vehicle_type,
            vehicle_name,
            vehicle_color,
            vehicle_year,
            license_plate,
            rating,
            total_trips,
            is_verified,
            status,
            user_profiles!driver_profiles_user_id_fkey (
              full_name,
              avatar_url
            )
          `)?.eq('vehicle_type', vehicleType)?.order('total_trips', { ascending: false })?.limit(1);
        driver = anyDriver?.[0] || null;
      }

      setDriverData(driver);

      // Fetch recent customer reviews for this vehicle type
      if (driver) {
        const { data: ratingsData } = await supabase?.from('order_ratings')?.select(`
            rating,
            review_text,
            created_at,
            reviewer_id,
            orders!order_ratings_order_id_fkey (
              vehicle_type,
              driver_id
            ),
            user_profiles!order_ratings_reviewer_id_fkey (
              full_name,
              avatar_url
            )
          `)?.eq('reviewer_role', 'customer')?.not('review_text', 'is', null)?.order('created_at', { ascending: false })?.limit(5);

        // Filter to reviews for this vehicle type
        const filtered = (ratingsData || [])?.filter(
          (r) => r?.orders?.vehicle_type === vehicleType
        );
        setReviews(filtered?.slice(0, 3));
      }
    } catch (err) {
      console.error('Failed to fetch driver profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const vehicleTypeLabels = {
    bike: 'Bicycle',
    boda: 'Boda Boda',
    tuktuk: 'Tuk Tuk',
    car: 'Car',
  };

  const vehicleEmojis = {
    bike: '🚲',
    boda: '🏍️',
    tuktuk: '🛺',
    car: '🚗',
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d?.toLocaleDateString('en-KE', { month: 'short', year: 'numeric' });
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name?.split(' ')?.map((n) => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header bar */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-[#0a1628]/8 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-xl">{vehicleEmojis?.[vehicleType]}</span>
            <div>
              <h2 className="text-base font-bold text-[#0a1628]">Driver Profile</h2>
              <p className="text-xs text-[#0a1628]/50">{vehicleTypeLabels?.[vehicleType] || vehicleLabel} · Top Rated</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-[#0a1628]/8 flex items-center justify-center hover:bg-[#0a1628]/15 transition-colors"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4 text-[#0a1628]">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="p-5 space-y-4">
              {/* Driver card skeleton */}
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-2xl bg-[#0a1628]/8 animate-pulse flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-5 w-32 rounded-full bg-[#0a1628]/10 animate-pulse" />
                  <div className="h-3 w-24 rounded-full bg-[#0a1628]/8 animate-pulse" />
                  <div className="h-3 w-20 rounded-full bg-[#0a1628]/6 animate-pulse" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[1, 2, 3]?.map((i) => (
                  <div key={i} className="h-16 rounded-xl bg-[#0a1628]/6 animate-pulse" />
                ))}
              </div>
              <div className="space-y-3">
                {[1, 2]?.map((i) => (
                  <div key={i} className="h-20 rounded-xl bg-[#0a1628]/6 animate-pulse" />
                ))}
              </div>
            </div>
          ) : !driverData ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#ffd700]/10 flex items-center justify-center mx-auto mb-3">
                <span className="text-3xl">{vehicleEmojis?.[vehicleType]}</span>
              </div>
              <p className="text-[#0a1628] font-semibold mb-1">No drivers yet</p>
              <p className="text-sm text-[#0a1628]/50">
                Be the first to book a {vehicleTypeLabels?.[vehicleType]} ride!
              </p>
            </div>
          ) : (
            <div className="p-5 space-y-5">
              {/* Driver Identity Card */}
              <div className="flex items-center gap-4">
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  {driverData?.user_profiles?.avatar_url ? (
                    <img
                      src={driverData?.user_profiles?.avatar_url}
                      alt={`${driverData?.user_profiles?.full_name || 'Driver'} profile photo`}
                      className="w-20 h-20 rounded-2xl object-cover border-2 border-[#ffd700]/30"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#0a1628] to-[#1a365d] flex items-center justify-center border-2 border-[#ffd700]/30">
                      <span className="text-2xl font-bold text-[#ffd700]">
                        {getInitials(driverData?.user_profiles?.full_name)}
                      </span>
                    </div>
                  )}
                  {/* Verified badge */}
                  {driverData?.is_verified && (
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-green-500 border-2 border-white flex items-center justify-center">
                      <svg viewBox="0 0 12 12" fill="none" className="w-3 h-3">
                        <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Name & Rating */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-[#0a1628] truncate">
                    {driverData?.user_profiles?.full_name || 'OnTheGo Driver'}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <StarRow rating={parseFloat(driverData?.rating) || 5} size="sm" />
                    <span className="text-sm font-bold text-[#0a1628]">
                      {parseFloat(driverData?.rating || 5)?.toFixed(1)}
                    </span>
                  </div>
                  {driverData?.is_verified && (
                    <div className="flex items-center gap-1 mt-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      <span className="text-xs text-green-600 font-medium">Verified Driver</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-2.5">
                <div className="bg-[#ffd700]/8 rounded-2xl p-3 text-center">
                  <div className="text-xl font-bold text-[#0a1628]">
                    {parseFloat(driverData?.rating || 5)?.toFixed(1)}
                  </div>
                  <div className="text-xs text-[#0a1628]/60 mt-0.5">Avg Rating</div>
                  <StarRow rating={parseFloat(driverData?.rating) || 5} size="sm" />
                </div>
                <div className="bg-[#0a1628]/5 rounded-2xl p-3 text-center">
                  <div className="text-xl font-bold text-[#0a1628]">
                    {(driverData?.total_trips || 0)?.toLocaleString()}
                  </div>
                  <div className="text-xs text-[#0a1628]/60 mt-0.5">Total Trips</div>
                  <div className="flex justify-center mt-1">
                    <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5 text-[#0a1628]/40">
                      <path d="M8 2l1.5 3 3.5.5-2.5 2.5.5 3.5L8 10l-3 1.5.5-3.5L3 5.5 6.5 5z" stroke="currentColor" strokeWidth="1.2" />
                    </svg>
                  </div>
                </div>
                <div className="bg-green-50 rounded-2xl p-3 text-center">
                  <div className="text-xl font-bold text-green-700">
                    {driverData?.status === 'online' ? '🟢' : driverData?.status === 'busy' ? '🟡' : '⚫'}
                  </div>
                  <div className="text-xs text-[#0a1628]/60 mt-0.5">Status</div>
                  <div className="text-xs font-semibold capitalize text-green-700 mt-0.5">
                    {driverData?.status || 'offline'}
                  </div>
                </div>
              </div>

              {/* Vehicle Info */}
              <div className="bg-[#0a1628]/4 rounded-2xl p-4">
                <h4 className="text-xs font-bold text-[#0a1628]/50 uppercase tracking-wider mb-3">Vehicle Info</h4>
                <div className="grid grid-cols-2 gap-y-2.5 gap-x-4">
                  <div>
                    <div className="text-xs text-[#0a1628]/40">Type</div>
                    <div className="text-sm font-semibold text-[#0a1628] capitalize">
                      {vehicleTypeLabels?.[vehicleType] || vehicleType}
                    </div>
                  </div>
                  {driverData?.vehicle_name && (
                    <div>
                      <div className="text-xs text-[#0a1628]/40">Model</div>
                      <div className="text-sm font-semibold text-[#0a1628]">{driverData?.vehicle_name}</div>
                    </div>
                  )}
                  {driverData?.vehicle_color && (
                    <div>
                      <div className="text-xs text-[#0a1628]/40">Color</div>
                      <div className="text-sm font-semibold text-[#0a1628] capitalize">{driverData?.vehicle_color}</div>
                    </div>
                  )}
                  {driverData?.vehicle_year && (
                    <div>
                      <div className="text-xs text-[#0a1628]/40">Year</div>
                      <div className="text-sm font-semibold text-[#0a1628]">{driverData?.vehicle_year}</div>
                    </div>
                  )}
                  {driverData?.license_plate && (
                    <div className="col-span-2">
                      <div className="text-xs text-[#0a1628]/40">Plate Number</div>
                      <div className="text-sm font-bold text-[#0a1628] font-mono tracking-widest">
                        {driverData?.license_plate}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Reviews */}
              <div>
                <h4 className="text-xs font-bold text-[#0a1628]/50 uppercase tracking-wider mb-3">
                  Recent Customer Reviews
                </h4>
                {reviews?.length === 0 ? (
                  <div className="bg-[#0a1628]/4 rounded-2xl p-4 text-center">
                    <p className="text-sm text-[#0a1628]/40">No reviews yet for this vehicle type</p>
                    <p className="text-xs text-[#0a1628]/30 mt-1">Be the first to leave a review!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {reviews?.map((review, idx) => (
                      <div key={idx} className="bg-white border border-[#0a1628]/8 rounded-2xl p-4 shadow-sm">
                        <div className="flex items-start gap-3">
                          {/* Reviewer avatar */}
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#0a1628]/20 to-[#0a1628]/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-[#0a1628]/60">
                              {getInitials(review?.user_profiles?.full_name)}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-semibold text-[#0a1628] truncate">
                                {review?.user_profiles?.full_name || 'Customer'}
                              </span>
                              <span className="text-xs text-[#0a1628]/40 flex-shrink-0">
                                {formatDate(review?.created_at)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 mt-0.5 mb-2">
                              <StarRow rating={review?.rating} size="sm" />
                              <span className="text-xs font-bold text-[#0a1628]/70">{review?.rating}/5</span>
                            </div>
                            {review?.review_text && (
                              <p className="text-sm text-[#0a1628]/70 leading-relaxed">
                                "{review?.review_text}"
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bottom padding */}
              <div className="h-2" />
            </div>
          )}
        </div>

        {/* Footer CTA */}
        <div className="px-5 py-4 border-t border-[#0a1628]/8 bg-white flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full py-3.5 rounded-2xl bg-[#0a1628] text-[#ffd700] font-bold text-sm hover:bg-[#1a365d] transition-colors active:scale-[0.98]"
          >
            Select {vehicleTypeLabels?.[vehicleType] || vehicleLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DriverProfileModal;
